/**
 * @spec docs/specs/features/staking-position-ui.md#design-system
 * @figma https://www.figma.com/design/Z2CmEcX1brcgv3hfhOWO07/Phenom-Poker?node-id=26237-88329
 *
 * Application header component matching Phenom Poker design system.
 * Contains logo, navigation links, and wallet/auth connection status.
 *
 * Mobile: Logo + Wallet + Hamburger menu
 * Desktop: Logo + Full nav + Wallet
 */
import { useState, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/cn';
import { routes } from '@/lib/routes';
import { ConnectWalletButton } from '@/components/wallet/ConnectWalletButton';
import { WalletStatus } from '@/components/wallet/WalletStatus';
import { SignInButton } from '@/components/auth/SignInButton';
import { MobileMenu } from './MobileMenu';
import { useWalletAddress } from '@/hooks/useWalletAddress';
import { useAuth } from '@/hooks/useAuth';
import Logo from '@/assets/images/logo.svg';
import DashboardIcon from '@/assets/icons/dashboard.svg?react';
import { useSelectedWallet } from '@/hooks/useSelectedWallet';

interface NavLinkProps {
  to: string;
  isActive: boolean;
  children: React.ReactNode;
  icon?: React.ReactNode;
  disabled?: boolean;
  comingSoon?: boolean;
}

/**
 * Individual navigation link with active state styling
 */
function NavLink({ to, isActive, children, icon, disabled, comingSoon }: NavLinkProps) {
  const baseStyles = cn(
    'flex items-center gap-1.5 h-8 px-3 rounded-full',
    'font-bold text-sm tracking-[-0.14px] whitespace-nowrap',
    'transition-all duration-200'
  );

  const activeStyles = 'bg-white/10 text-white';
  const inactiveStyles = 'text-white/50 hover:text-white/70 hover:bg-white/5';
  const disabledStyles = 'text-white/30 cursor-not-allowed';

  if (disabled) {
    return (
      <div className={cn(baseStyles, disabledStyles)}>
        {icon && <span className="size-5">{icon}</span>}
        <div className="flex flex-col items-start justify-center">
          <span>{children}</span>
          {comingSoon && (
            <span className="text-[8px] font-extrabold uppercase tracking-[0.8px] text-accent-orange leading-[10px]">
              Coming Soon
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <Link to={to} className={cn(baseStyles, isActive ? activeStyles : inactiveStyles)}>
      {icon && <span className="size-5">{icon}</span>}
      <span>{children}</span>
    </Link>
  );
}

/**
 * Hamburger menu button for mobile
 */
function HamburgerButton({ isOpen, onClick }: { isOpen: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'lg:hidden flex flex-col justify-center items-center',
        'w-10 h-10 rounded-lg',
        'hover:bg-white/5 transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-purple'
      )}
      aria-label={isOpen ? 'Close menu' : 'Open menu'}
      aria-expanded={isOpen}
    >
      <span
        className={cn(
          'block w-5 h-0.5 bg-white transition-all duration-300',
          isOpen ? 'rotate-45 translate-y-[3px]' : ''
        )}
      />
      <span
        className={cn(
          'block w-5 h-0.5 bg-white mt-1 transition-all duration-300',
          isOpen ? 'opacity-0' : ''
        )}
      />
      <span
        className={cn(
          'block w-5 h-0.5 bg-white mt-1 transition-all duration-300',
          isOpen ? '-rotate-45 -translate-y-[7px]' : ''
        )}
      />
    </button>
  );
}

/**
 * Application header with logo, navigation, and wallet/auth connection
 */
export function Header() {
  const location = useLocation();
  const { isConnected } = useWalletAddress();
  const { isAuthenticated, email, isWalletOnlyMode } = useAuth();
  const { address: selectedAddress } = useSelectedWallet();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleCloseMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []);

  const handleToggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen((prev) => !prev);
  }, []);

  const isHome = location.pathname === routes.home();
  const isAddress = location.pathname.startsWith('/address/');
  const isStake = location.pathname === routes.stake();
  const isSwap = location.pathname === routes.swap();

  return (
    <>
      <header className="sticky top-0 z-50 backdrop-blur-sm border-b border-white/5">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-[60px] lg:h-[70px] gap-4">
            {/* Logo */}
            <Link to={routes.home()} className="flex items-center shrink-0">
              <img src={Logo} alt="Phenom Poker" className="h-6 lg:h-[30px] w-auto" />
            </Link>

            {/* Desktop Navigation - hidden on mobile */}
            <nav className="hidden lg:flex items-center gap-1 flex-1 justify-center">
              <NavLink
                to={routes.home()}
                isActive={isHome}
                icon={<DashboardIcon className="size-5" aria-hidden="true" />}
              >
                Dashboard
              </NavLink>

              {(isConnected || isAuthenticated) && selectedAddress && (
                <NavLink to={routes.address(selectedAddress)} isActive={isAddress}>
                  Portfolio
                </NavLink>
              )}

              {isAuthenticated && (
                <NavLink to={routes.stake()} isActive={isStake}>
                  Stake
                </NavLink>
              )}

              {isAuthenticated && (
                <NavLink to={routes.swap()} isActive={isSwap}>
                  Swap
                </NavLink>
              )}

              <a
                href="https://play.phenompoker.com"
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  'flex items-center gap-1.5 h-8 px-3 rounded-full',
                  'font-bold text-sm tracking-[-0.14px] whitespace-nowrap',
                  'transition-all duration-200',
                  'text-white/50 hover:text-white/70 hover:bg-white/5'
                )}
              >
                Play
              </a>
            </nav>

            {/* Right side: Auth/Wallet + Mobile menu button */}
            <div className="flex items-center gap-2 shrink-0">
              {/* Email - desktop only */}
              {isAuthenticated && email && (
                <span className="text-xs text-text-muted hidden xl:block max-w-[150px] truncate">
                  {email}
                </span>
              )}

              {/* Wallet/Auth - always visible */}
              {isConnected || isAuthenticated ? (
                <WalletStatus />
              ) : (
                <div className="flex items-center gap-2">
                  <ConnectWalletButton size="sm" />
                  <SignInButton size="sm" variant="outline" className="hidden sm:flex" />
                </div>
              )}

              {/* Sign in prompt for wallet-only mode - hidden on mobile */}
              {isWalletOnlyMode && (
                <SignInButton size="sm" variant="ghost" className="hidden sm:flex">
                  Sign In
                </SignInButton>
              )}

              {/* Hamburger menu - mobile only */}
              <HamburgerButton isOpen={isMobileMenuOpen} onClick={handleToggleMobileMenu} />
            </div>
          </div>
        </div>
      </header>

      {/* Mobile menu drawer */}
      <MobileMenu
        isOpen={isMobileMenuOpen}
        onClose={handleCloseMobileMenu}
        isConnected={isConnected}
        isAuthenticated={isAuthenticated}
        selectedAddress={selectedAddress}
        email={email}
      />
    </>
  );
}
